<template>
  <div
    class="absolute inset-x-0 bottom-full z-10 mb-2 overflow-hidden rounded-2xl border border-charcoal-200 bg-white shadow-lg dark:border-charcoal-700 dark:bg-charcoal-850">
    <div v-if="hint" class="px-4 py-3 text-sm italic text-charcoal-400 dark:text-charcoal-500">
      {{ hint }}
    </div>
    <div v-else-if="loading && !results.length" class="px-4 py-3 text-sm italic text-charcoal-400 dark:text-charcoal-500">
      Searching...
    </div>
    <div v-else-if="!results.length" class="px-4 py-3 text-sm italic text-charcoal-400 dark:text-charcoal-500">
      No matches found.
    </div>
    <ul v-else ref="listEl" class="agent-chat-scrollbar max-h-60 overflow-y-auto py-1">
      <li v-for="(result, index) in results" :key="result.key" :ref="(el) => (itemRefs[index] = el)">
        <button type="button"
          class="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors"
          :class="index === activeIndex
            ? 'bg-iris-600 text-white'
            : 'text-charcoal-800 hover:bg-charcoal-50 dark:text-charcoal-200 dark:hover:bg-charcoal-800'"
          @mouseenter="activeIndex = index" @mousedown.prevent="emit('select', result)">
          <Icon :icon="iconFor(result)" class="size-4 shrink-0" />
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-medium">{{ result.label }}</span>
            <span class="block truncate text-xs"
              :class="index === activeIndex ? 'text-iris-100' : 'text-charcoal-500 dark:text-charcoal-400'">{{
              result.sublabel }}</span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { nextTick, reactive, ref, watch } from 'vue'
import { Icon } from '@iconify/vue'

const props = defineProps({
  hint: { type: String, default: null },
  loading: { type: Boolean, default: false },
  results: { type: Array, default: () => [] },
})

const emit = defineEmits(['select'])

// v-model so hovering an option can move the keyboard selection without the
// parent needing its own hover-tracking wiring.
const activeIndex = defineModel('activeIndex', { default: 0 })

// Arrow-key navigation only moves `activeIndex` — nothing about that touches
// the DOM, so the highlighted <li> can end up outside the scrollable list's
// visible area (you'd have to grab the mouse scroll wheel to see it). Scroll
// it into view whenever the highlight moves.
const listEl = ref(null)
const itemRefs = reactive([])

watch(activeIndex, (index) => {
  nextTick(() => {
    itemRefs[index]?.scrollIntoView({ block: 'nearest' })
  })
})

watch(
  () => props.results,
  () => {
    itemRefs.length = 0
    if (listEl.value) listEl.value.scrollTop = 0
  }
)

const ICONS = {
  Account: 'heroicons:building-office',
  Opportunity: 'heroicons:sparkles',
  Contact: 'heroicons:user',
  command: 'heroicons:bolt',
}

function iconFor(result) {
  return ICONS[result.recordType] ?? ICONS.command
}
</script>
